import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Dialog} from '@components/common/base/dialog/dialog'
import {Input} from '@components/common/base/input'
import {InputApi} from '@components/common/base/input/types'
import {useInputKeyDown} from '@containers/hooks'
import {checkProjectPrefix} from '@extensions/id-tasks/check-project-prefix'
import {idTasksApi, idTasksStore} from '@extensions/id-tasks/id-tasks-store'
import {setProjectId} from '@extensions/id-tasks/set-project-id'
import {useCompanyProjectsStore} from '../company-projects-store'

export const EditIdProjectPopup = observer(() => {
  const [state] = useCompanyProjectsStore()

  if (!state.showPopupEditId) {
    return null
  }

  return <EditIdProjectPopupBase />
})

const processProjPrefix = (text: string): string => {
  if (!text) {
    return ''
  }
  const removedOtherSymbols = text.replace(/[^a-zA-Zа-яА-Я0-9]/g, '')
  const shortText = removedOtherSymbols.slice(0, 5)
  return shortText.toUpperCase().slice(0, 5)
}

const EditIdProjectPopupBase = observer(() => {
  const [state, store] = useCompanyProjectsStore()
  const [prefix, setPrefix] = useState<string>('')
  const [origPrefix, setOrigPrefix] = useState<string>('')
  const [prefixInvalid, setPrefixInvalid] = useState<boolean>(false)
  const [isBusy, setBusy] = useState<boolean>(false)

  const projectId = state.showPopupEditId

  const cannotSubmit =
    !projectId || !prefix || isBusy || prefixInvalid || origPrefix === prefix

  useEffect(() => {
    const prefix = !state.showPopupEditId
      ? ''
      : idTasksApi.getIdProject(state.showPopupEditId) || ''
    setOrigPrefix(prefix)
    setPrefix(prefix)
  }, [])

  const onChangeProjId = async (api: InputApi): Promise<void> => {
    const prefix = processProjPrefix(api.text)

    setPrefix(prefix)

    if (origPrefix === prefix) {
      setPrefixInvalid(false)
      setBusy(false)
      return
    }

    const invalid = /[^a-zA-Z0-9]/g.test(prefix)
    setPrefixInvalid(invalid)

    if (!invalid) {
      const data = prefix === 'ID' ? true : await checkProjectPrefix(prefix)
      setBusy(data)
    }
  }

  const onSubmit = () => {
    if (cannotSubmit) {
      return
    }

    void Tracking.send(TrackingEvent.projectSettingsChangePrefix)

    void setProjectId(projectId, prefix)

    idTasksApi.setIdProject(projectId, prefix)
    idTasksStore.state.modified = Date.now()
    store.resetProjectIdEditing?.()
  }

  const inputRef = useRef<HTMLInputElement>()
  useInputKeyDown({
    ref: inputRef,
    save: () => {
      onSubmit()
      return true
    },
    cancel: () => {
      store.resetProjectIdEditing?.()
      return true
    }
  })

  return (
    <Dialog
      close={store.resetProjectIdEditing}
      submit={onSubmit}
      title={Lang.msg.proj.editIdProjectPopupTitle}
      width={500}
      primaryBtnDisabled={cannotSubmit}
    >
      <div className={'relative'}>
        <Input
          ref={inputRef}
          onChange={onChangeProjId}
          placeholder={Lang.msg.idProject}
          value={prefix}
          isValid={() => (isBusy ? 'error' : 'empty')}
          autoFocus
          className="max-w-200"
          rounded={'8'}
        />
        {isBusy && !prefixInvalid && (
          <div className="text-status-error micro-regular absolute top-42 left-0 cursor-default">
            {prefix.trim() === 'ID'
              ? Lang.msg.idProjectBusyBySystem
              : Lang.msg.idProjectBusy}
          </div>
        )}
        {prefixInvalid && (
          <div className="text-status-error micro-regular absolute top-42 left-0 cursor-default">
            {Lang.msg.idProjectInvalid}
          </div>
        )}
      </div>
    </Dialog>
  )
})
